"use client";

import Script from "next/script";
import { useEffect, useState } from "react";
import { readStoredConsent, type StoredConsent } from "@/lib/consent";

/**
 * Google Analytics (GA4) – lädt ERST nach Einwilligung „Statistik“.
 *
 *   • Ohne Einwilligung geht kein einziger Request an Google
 *     (Audit 2026-10-21: gtag.js lud vorher 178 KB vor dem Consent).
 *   • Consent Mode v2: Default „denied“, danach Update je nach Auswahl.
 *   • Nonce aus der Middleware (CSP) – ohne ihn würde das Inline-Skript
 *     vom Browser blockiert.
 *   • Widerruf: Skript bleibt geladen, Consent wird auf „denied“ gesetzt.
 *
 * Tracking-ID kommt aus ENV `NEXT_PUBLIC_GA4_ID` und fällt im Default
 * auf die produktive ID zurück. Bei leerem Wert wird gar nichts geladen.
 */
const GA_ID = process.env.NEXT_PUBLIC_GA4_ID || "G-NCR9MFXVLB";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer?: any[];
    gtag?: (...args: unknown[]) => void;
  }
}

type Flags = { statistics: boolean; marketing: boolean };

function consentUpdate(flags: Flags): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const stat = flags.statistics ? "granted" : "denied";
  const mkt = flags.marketing ? "granted" : "denied";
  window.gtag("consent", "update", {
    analytics_storage: stat,
    ad_storage: mkt,
    ad_user_data: mkt,
    ad_personalization: mkt,
  });
}

export function GoogleAnalytics({
  nonce,
  initialConsent,
}: {
  nonce?: string;
  initialConsent: StoredConsent | null;
}) {
  const [flags, setFlags] = useState<Flags>({
    statistics: !!initialConsent?.statistics,
    marketing: !!initialConsent?.marketing,
  });

  useEffect(() => {
    // Einwilligung, die nur in localStorage lag (vor der Cookie-Umstellung)
    const stored = readStoredConsent();
    if (stored) setFlags({ statistics: stored.statistics, marketing: stored.marketing });
    const onChange = (e: Event) => {
      const d = (e as CustomEvent<StoredConsent>).detail;
      if (d) setFlags({ statistics: !!d.statistics, marketing: !!d.marketing });
    };
    window.addEventListener("cookie-consent:changed", onChange);
    return () => window.removeEventListener("cookie-consent:changed", onChange);
  }, []);

  useEffect(() => {
    consentUpdate(flags);
  }, [flags]);

  if (!GA_ID || !flags.statistics) return null;

  const mkt = flags.marketing ? "granted" : "denied";
  return (
    <>
      <Script
        id="gtag-src"
        nonce={nonce}
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
        strategy="lazyOnload"
      />
      <Script id="gtag-init" nonce={nonce} strategy="lazyOnload">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
            wait_for_update: 500,
          });
          gtag('consent', 'update', {
            analytics_storage: 'granted',
            ad_storage: '${mkt}',
            ad_user_data: '${mkt}',
            ad_personalization: '${mkt}',
          });
          gtag('js', new Date());
          gtag('config', '${GA_ID}', { anonymize_ip: true });
        `}
      </Script>
    </>
  );
}
